/**
 * Image URL helpers.
 *
 * Centralises provider-specific transforms so swapping the media backend
 * (royalty-free Unsplash placeholders today → Cloudinary/CMS assets in
 * production) is a ONE-FILE change with no component edits. Providers that
 * support on-the-fly resizing get responsive srcSets + a blurred LQIP; any
 * other URL falls back untouched to its original form.
 */

// Hosts that accept imgix-style `w`/`q`/`auto`/`blur` query transforms.
const RESIZABLE_HOSTS = ['images.unsplash.com', 'res.cloudinary.com'];

function resizable(url: string): URL | null {
  try {
    const u = new URL(url);
    return RESIZABLE_HOSTS.some((h) => u.hostname.endsWith(h)) ? u : null;
  } catch {
    return null;
  }
}

/** A width-specific variant of an image URL (modern format via `auto=format`). */
export function imageUrl(url: string, width: number): string {
  const u = resizable(url);
  if (!u) return url;
  u.searchParams.set('auto', 'format'); // serves AVIF/WebP to browsers that accept it
  u.searchParams.set('fit', u.searchParams.get('fit') ?? 'crop');
  u.searchParams.set('w', String(width));
  u.searchParams.set('q', u.searchParams.get('q') ?? '70');
  return u.toString();
}

/** Candidate widths for responsive srcSets (covers mobile → 2× desktop). */
const DEFAULT_WIDTHS = [400, 640, 828, 1080, 1600];

/** Build a responsive srcSet ("url 400w, url 640w, …"). Undefined if unsupported. */
export function buildSrcSet(url: string, widths: number[] = DEFAULT_WIDTHS): string | undefined {
  if (!resizable(url)) return undefined;
  return widths.map((w) => `${imageUrl(url, w)} ${w}w`).join(', ');
}

/** A tiny, heavily-blurred placeholder (LQIP) for blur-up while the image loads. */
export function lqip(url: string): string | undefined {
  const u = resizable(url);
  if (!u) return undefined;
  u.searchParams.set('auto', 'format');
  u.searchParams.set('fit', u.searchParams.get('fit') ?? 'crop');
  u.searchParams.set('w', '32');
  u.searchParams.set('q', '30');
  u.searchParams.set('blur', '120');
  return u.toString();
}
